<script setup lang="ts">
import { ref, watch, Ref } from "vue";
import { debounce } from "vue-debounce";
import { useApiClient } from "@/api/client";
import EditScreenPropertiesDialog from "@/modules/campaign_planner/components/screens/EditScreenPropertiesDialog.vue";
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;

type TriStateFilter = null | boolean;

interface TableOptions {
	page: number;
	itemsPerPage: number;
	sortBy: { key: string; order: "asc" | "desc" }[];
}

const headers = [
	{ title: "ID", key: "id", align: "start", sortable: true },
	{ title: "Reference", key: "reference_id", align: "start", sortable: true },
	{ title: "Inventory", key: "inventory_type", align: "start", sortable: true },
	{ title: "Size", key: "size_label", align: "start", sortable: true },
	{ title: "Name", key: "name", align: "start", sortable: true },
	{ title: "Active", key: "active", align: "center", sortable: true },
	{ title: "Hidden", key: "is_hidden", align: "center", sortable: true },
	{
		title: "Actions",
		key: "actions",
		align: "end",
		sortable: false,
		width: 80,
	},
] as const;

const screens: Ref<ScreenDO[]> = ref([]);
const loading: Ref<boolean | string> = ref(false);
const total = ref(0);
const search = ref("");
const filterText = ref("");
const tableOptions = ref<TableOptions>({
	page: 1,
	itemsPerPage: 25,
	sortBy: [],
});

const filters = ref<{
	active: TriStateFilter;
	is_hidden: TriStateFilter;
	has_reference: TriStateFilter;
	inventory_type: string | null;
}>({
	active: true,
	is_hidden: null,
	has_reference: null,
	inventory_type: null,
});

const isEditDialogVisible = ref(false);
const screenToEdit = ref<ScreenDO | null>(null);

watch(filterText, () => (search.value = String(Date.now())));
watch(filters, () => (search.value = String(Date.now())), { deep: true });

const buildFilterParams = (): Record<string, string> => {
	const params: Record<string, string> = {};

	for (const [key, value] of Object.entries(filters.value)) {
		if (value === null) continue;
		params[`filter[${key}]`] =
			typeof value === "boolean" ? (value ? "true" : "false") : value;
	}

	if (filterText.value) params["filter[query]"] = filterText.value;

	return params;
};

const loadScreens = debounce(async (options: TableOptions) => {
	tableOptions.value = options;
	loading.value = "secondary";

	const { page, itemsPerPage, sortBy } = options;
	const sort = sortBy[0]
		? (sortBy[0].order === "desc" ? "-" : "") + sortBy[0].key
		: undefined;

	const response = await useApiClient("campaignPlanner").getScreens({
		page,
		per_page: itemsPerPage === -1 ? undefined : itemsPerPage,
		sort,
		...buildFilterParams(),
	});

	if (response.isOk()) {
		screens.value = response.value.data;
		total.value = response.value.total;
	}

	loading.value = false;
}, 300);

const editScreen = (screen: ScreenDO) => {
	screenToEdit.value = screen;
	isEditDialogVisible.value = true;
};
</script>

<template>
	<edit-screen-properties-dialog
		v-model="isEditDialogVisible"
		:screen-to-edit="screenToEdit"
		@update-list="loadScreens(tableOptions)"
	/>

	<v-card elevation="1" class="mt-5">
		<div class="card-header">
			<v-text-field
				v-model="filterText"
				prepend-inner-icon="fa: fa-regular fa-magnifying-glass"
				placeholder="Search screens…"
				clearable
				hide-details
				variant="filled"
				density="compact"
			/>

			<div class="filter-strip">
				<div class="filter-group">
					<span class="text-caption text-medium-emphasis">Active</span>
					<v-btn-toggle
						v-model="filters.active"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
						rounded="lg"
					>
						<v-btn :value="null" size="small">All</v-btn>
						<v-btn :value="true" size="small">Active</v-btn>
						<v-btn :value="false" size="small">Inactive</v-btn>
					</v-btn-toggle>
				</div>

				<div class="filter-group">
					<span class="text-caption text-medium-emphasis">Visibility</span>
					<v-btn-toggle
						v-model="filters.is_hidden"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
						rounded="lg"
					>
						<v-btn :value="null" size="small">All</v-btn>
						<v-btn :value="false" size="small">Visible</v-btn>
						<v-btn :value="true" size="small">Hidden</v-btn>
					</v-btn-toggle>
				</div>

				<div class="filter-group">
					<span class="text-caption text-medium-emphasis">Reference</span>
					<v-btn-toggle
						v-model="filters.has_reference"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
						rounded="lg"
					>
						<v-btn :value="null" size="small">All</v-btn>
						<v-btn :value="true" size="small">With ref</v-btn>
						<v-btn :value="false" size="small">No ref</v-btn>
					</v-btn-toggle>
				</div>

				<div class="filter-group">
					<span class="text-caption text-medium-emphasis">Inventory</span>
					<v-btn-toggle
						v-model="filters.inventory_type"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
						rounded="lg"
					>
						<v-btn :value="null" size="small">All</v-btn>
						<v-btn value="Digital" size="small">Digital</v-btn>
						<v-btn value="Static" size="small">Static</v-btn>
					</v-btn-toggle>
				</div>
			</div>
		</div>

		<v-divider />

		<v-data-table-server
			:headers="headers"
			:items="screens"
			:items-per-page="tableOptions.itemsPerPage"
			:page="tableOptions.page"
			:items-length="total"
			:loading="loading"
			:search="search"
			:multi-sort="false"
			class="elevation-0"
			@update:options="loadScreens"
		>
			<template v-slot:item.active="{ item }">
				<div v-if="item.active">
					<v-icon color="success" size="small">
						fa: fa-solid fa-circle-check
					</v-icon>
				</div>
				<div v-else>
					<v-icon color="medium-emphasis" size="small">
						fa: fa-regular fa-circle-xmark
					</v-icon>
				</div>
			</template>

			<template v-slot:item.is_hidden="{ item }">
				<div v-if="item.is_hidden">
					<v-icon color="warning" size="small">
						fa: fa-solid fa-eye-slash
					</v-icon>
				</div>
				<div v-else>
					<v-icon color="medium-emphasis" size="small">
						fa: fa-regular fa-eye
					</v-icon>
				</div>
			</template>

			<template v-slot:item.name="{ item }">
				<v-tooltip
					:text="item.name ?? ''"
					location="top"
					:disabled="!item.name || item.name.length <= 20"
				>
					<template v-slot:activator="{ props }">
						<span class="truncate-name" v-bind="props">{{
							item.name ?? "-"
						}}</span>
					</template>
				</v-tooltip>
			</template>

			<template v-slot:item.size_label="{ item }">
				{{ item.size?.label ?? "-" }}
			</template>

			<template v-slot:item.inventory_type="{ item }">
				<v-chip v-if="item.inventory_type" size="small" variant="tonal">
					{{ item.inventory_type }}
				</v-chip>
				<span v-else>-</span>
			</template>

			<template v-slot:item.actions="{ item }">
				<v-btn
					size="x-small"
					icon="fa: fa-regular fa-pen"
					color="primary"
					variant="text"
					@click="editScreen(item)"
				/>
			</template>
		</v-data-table-server>
	</v-card>
</template>

<style scoped lang="scss">
.card-header {
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.filter-strip {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 20px;
	align-items: flex-end;
}

.filter-group {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.truncate-name {
	display: inline-block;
	max-width: 30ch;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	vertical-align: bottom;
}
</style>
